import { useSwitch } from '@mui/base';
import cn from 'classnames';
import { forwardRef } from 'react';

import type { ForwardedRef, ReactElement } from 'react';

import type { ToggleProps } from './toggle.types';

import './toggle.scss';

/**
 * Toggle represents an action that changes a state by clicking on it.
 * They are usually used for actions or options that have an immediate effect on the UI.
 * They usually do not need to be confirmed or reviewed.
 *
 * ### Usage
 *
 * ```tsx
 * import { Toggle } from '@bloomreach/react-banana-ui';
 *
 * export default function MyCustomToggle() {
 *   return <Toggle name="fieldName" />;
 * }
 * ```
 */
const Toggle = forwardRef((props: ToggleProps, forwardedRef: ForwardedRef<HTMLInputElement> | null): ReactElement => {
  const {
    checked: checkedProp,
    className,
    defaultChecked,
    disabled: disabledProp,
    inputProps,
    name,
    onChange: onChangeProp,
    readOnly: readOnlyProp,
    required,
    ...rest // Capture any other properties to pass them to the root element, like `aria-*`, `data-*`, etc.
  } = props;
  const { checked, disabled, focusVisible, getInputProps, readOnly } = useSwitch(props);

  const { onChange, ...inputPropsInternal } = getInputProps({
    /* @ts-expect-error The `externalProps` in `getInputProps` doesn't have `name` due to a wrong type
    `HTMLAttributes<HTMLInputElement>` instead of `InputHTMLAttributes<HTMLInputElement>`. */
    name,
    ...inputProps,
    className: cn('rbui-toggle__input', inputProps?.className),
  });

  return (
    <span
      aria-checked={checked}
      aria-disabled={disabled}
      aria-readonly={readOnly}
      className={cn(className, 'rbui-toggle', {
        'rbui-toggle--checked': checked,
        'rbui-toggle--disabled': disabled,
        'rbui-toggle--focus-visible': focusVisible,
        'rbui-toggle--readonly': readOnly,
      })}
      {...rest}
    >
      <div className={cn('rbui-toggle__track')}>
        <div className={cn('rbui-toggle__thumb')} />
      </div>
      <input
        {...inputPropsInternal}
        // By default the `input` with type `checkbox` allows to change the value when it is read-only.
        onChange={readOnly ? undefined : onChange}
        ref={forwardedRef}
      />
    </span>
  );
});

Toggle.displayName = 'Toggle';

export default Toggle;
